import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/swal';
import { CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

export default function PaymentApprovalPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approvalData, setApprovalData] = useState<any>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApprovalData = async () => {
      const token = searchParams.get('token');
      const unitId = searchParams.get('unitId');

      if (!token || !invoiceId || !unitId) {
        setError('Invalid approval link. Missing required parameters.');
        setLoading(false);
        return;
      }

      try {
        // Get approval token data
        const approvalDoc = await getDoc(doc(db, 'paymentApprovals', token));
        
        if (!approvalDoc.exists()) {
          setError('Approval link not found or has expired.');
          setLoading(false);
          return;
        }

        const approval = approvalDoc.data();
        
        // Check if expired
        if (approval.expiresAt?.toDate() < new Date()) {
          setError('This approval link has expired. Please request a new link.');
          setLoading(false);
          return;
        }

        // Check if already approved
        if (approval.approved) {
          setError('This payment has already been approved.');
          setLoading(false);
          return;
        }

        // Verify unitId matches
        if (approval.unitId !== unitId || approval.invoiceId !== invoiceId) {
          setError('Invalid approval link. Parameters do not match.');
          setLoading(false);
          return;
        }

        // Get invoice
        const invoiceDoc = await getDoc(
          doc(db, 'units', unitId, 'events', approval.eventId, 'invoices', invoiceId)
        );

        if (!invoiceDoc.exists()) {
          setError('Invoice not found.');
          setLoading(false);
          return;
        }

        // Get unit to check permissions
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (!unitDoc.exists()) {
          setError('Unit not found.');
          setLoading(false);
          return;
        }

        const unit = unitDoc.data();
        const members = unit.members || [];
        
        // Check if user is secretary or treasurer
        if (currentUser) {
          const member = members.find((m: any) => m.uid === currentUser.uid);
          const hasPermission = member && ['Secretary', 'Treasurer'].includes(member.role);
          
          if (!hasPermission) {
            setError('You do not have permission to approve this payment. Only Secretary or Treasurer can approve payments.');
            setLoading(false);
            return;
          }
        }

        setApprovalData(approval);
        setInvoice({
          id: invoiceDoc.id,
          ...invoiceDoc.data(),
        });
        setLoading(false);
      } catch (error: any) {
        console.error('Error fetching approval data:', error);
        setError('Failed to load approval data. Please try again.');
        setLoading(false);
      }
    };

    fetchApprovalData();
  }, [invoiceId, searchParams, currentUser]);

  const handleApprove = async () => {
    if (!approvalData || !invoice) return;

    setApproving(true);
    try {
      const approvePayment = httpsCallable(functions, 'approvePayment');
      
      await approvePayment({
        approvalToken: searchParams.get('token'),
        unitId: approvalData.unitId,
        eventId: approvalData.eventId,
        invoiceId: approvalData.invoiceId,
        userId: approvalData.userId,
        amount: approvalData.amount,
      });

      showSuccess('Payment approved successfully! The invoice has been updated.');
      
      // Refetch invoice data to show updated status
      const invoiceDoc = await getDoc(
        doc(db, 'units', approvalData.unitId, 'events', approvalData.eventId, 'invoices', approvalData.invoiceId)
      );
      
      if (invoiceDoc.exists()) {
        const invoiceData = invoiceDoc.data();
        setInvoice({
          id: invoiceDoc.id,
          ...invoiceData,
          createdAt: invoiceData.createdAt?.toDate ? invoiceData.createdAt.toDate() : invoiceData.createdAt,
          updatedAt: invoiceData.updatedAt?.toDate ? invoiceData.updatedAt.toDate() : invoiceData.updatedAt,
        });
      }
      
      // Refetch approval data to mark as approved
      const approvalDoc = await getDoc(doc(db, 'paymentApprovals', searchParams.get('token') || ''));
      if (approvalDoc.exists()) {
        const approvalData = approvalDoc.data();
        setApprovalData({
          ...approvalData,
          createdAt: approvalData.createdAt?.toDate ? approvalData.createdAt.toDate() : approvalData.createdAt,
          expiresAt: approvalData.expiresAt?.toDate ? approvalData.expiresAt.toDate() : approvalData.expiresAt,
        });
      }
    } catch (error: any) {
      console.error('Error approving payment:', error);
      showError(error.message || 'Failed to approve payment. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading approval details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                Approval Error
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-2xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Approve Payment</h1>
            <p className="text-muted-foreground">Review and approve the bank transfer payment</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>Please verify the payment has been received before approving</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Invoice Number</label>
                    <div className="text-lg font-semibold">{invoice.invoiceNumber || invoice.id}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Amount</label>
                    <div className="text-lg font-semibold">{formatCurrency(approvalData.amount)}</div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Claimed Payment Date</label>
                  <div className="text-sm">
                    {approvalData.createdAt?.toDate 
                      ? formatDate(approvalData.createdAt.toDate())
                      : 'Unknown'}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">Current Invoice Status</label>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      invoice.status === 'paid' ? 'default' :
                      invoice.status === 'voided' ? 'outline' :
                      invoice.status === 'pending_verification' ? 'secondary' :
                      'warning'
                    }>
                      {invoice.status === 'voided' ? 'VOIDED' :
                      invoice.status === 'pending_verification' 
                        ? 'PENDING VERIFICATION' 
                        : invoice.status?.toUpperCase() || 'OUTSTANDING'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Outstanding: {formatCurrency(invoice.outstandingBalance || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {invoice.status === 'paid' || approvalData?.approved ? (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-green-900 mb-1">Payment Approved</h4>
                      <p className="text-sm text-green-800">
                        This payment has been successfully approved. The invoice status has been updated to paid.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-yellow-900 mb-1">Before Approving</h4>
                        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                          <li>Verify the payment has been received in the unit's bank account</li>
                          <li>Check the payment reference matches the invoice number</li>
                          <li>Confirm the amount matches the outstanding balance</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      onClick={handleApprove}
                      className="flex-1"
                      size="lg"
                      disabled={approving || invoice.status === 'paid' || invoice.status === 'voided'}
                    >
                      {approving ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Approve Payment
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      disabled={approving}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

